/**
 * Step-3 pop-ups, worded as the prototype's (docs/prototype.html newReq 1123, newInternalReq 3446, triageModal
 * 1112 + 3467, delayModal 3474, notYetModal 3489, newEsc 1186, newCommit 1162, askProof 1493, workAdd 2777).
 * Each one makes ONE contract call (routes.step3) and leaves the rules to the server: who may do it, the
 * clocks, the linked task, the audit row with both people when acting as someone.
 *
 * Creating calls carry a fresh Idempotency-Key per pop-up, so a double click cannot create twice.
 */
import { useMemo, useState } from 'react';
import {
  INTERNAL_REQUEST_TYPE_LABEL,
  NEED_DAYS,
  type InternalRequest,
  type InternalRequestType,
  type NeedDays,
  type RequestPurpose,
  type RequestVia,
  type Severity,
} from '@amplispot/contracts';
import { ApiFailure, call, newIdempotencyKey } from '../../api';
import { useClient, useClients, useUsers } from '../../lib/data';
import { firstName, todayIso } from '../../lib/format';
import {
  needLabel,
  needLower,
  dateNeedLabel,
  REQUEST_FILE_MAX_COUNT,
  REQUEST_FILE_TOO_BIG,
  requestFileTooBig,
  uploadRequestFile,
  useRequestOptions,
} from '../../lib/requests';
import { useSession } from '../../lib/session';
import { addDays, daysBetween, useWhose, useWorkWrite } from '../../lib/work';
import { Field, Modal, errorText, useFail, useToast } from '../../ui/kit';
import { AddTaskModal } from '../work/TaskModals';
import { NewItemModal } from '../work/ApprovalsBoard';
import { HistoryImportModal } from '../work/HistoryImport';

type Close = () => void;

/** Who the server gives work to (routes/requests.ts ASSIGNABLE): the founder, the Ops AD and Ops members. */
const OPS_ROLES: readonly string[] = ['founder', 'ops_ad', 'ops_member'];

const Foot = ({ onClose, label, busy, onGo, tone = 'pri', cancel = 'Cancel' }: { onClose: Close; label: string; busy: boolean; onGo: () => void; tone?: string; cancel?: string }) => (
  <>
    <div className="grow" />
    <button className="btn" onClick={onClose}>
      {cancel}
    </button>
    <button className={`btn ${tone}`} onClick={onGo} disabled={busy} aria-busy={busy}>
      {busy ? 'Saving…' : label}
    </button>
  </>
);

const NEED_OPTIONS = NEED_DAYS as readonly NeedDays[];

// ---------------------------------------------------------------- proof ("Show it is done")

export type ProofBody = {
  proof_kind: 'link' | 'note';
  proof_url?: string;
  proof_text?: string;
  no_attachment_needed?: boolean;
};

/**
 * Prototype `askProof(kind)` for a request, an escalation or a client task: a link, or "no attachment needed"
 * with a one-line note. Upload arrives with object storage (later step), so it is not offered yet.
 */
export function ProofModal(props: { noun: 'request' | 'escalation' | 'task'; title: string; onClose: Close; submit: (b: ProofBody) => Promise<unknown>; onDone?: () => void }) {
  const { errs, fail, toast } = useFail(['proof_url', 'proof_text', 'note']);
  const [link, setLink] = useState('');
  const [none, setNone] = useState(false);
  const [note, setNote] = useState('');
  const [local, setLocal] = useState('');
  const save = useWorkWrite((b: ProofBody) => props.submit(b));
  const go = () => {
    if (!link.trim() && !none) return setLocal('Paste a link, or tick No attachment needed');
    if (note.trim().length < 3) return setLocal('Say in a line what was done');
    setLocal('');
    const body: ProofBody = link.trim()
      ? { proof_kind: 'link', proof_url: link.trim(), proof_text: note.trim() }
      : { proof_kind: 'note', proof_text: note.trim(), no_attachment_needed: true };
    save.mutate(body, {
      onSuccess: () => {
        toast('Closed with proof');
        props.onDone?.();
        props.onClose();
      },
      onError: fail,
    });
  };
  return (
    <Modal title="Show it is done" onClose={props.onClose} footer={<Foot onClose={props.onClose} cancel="Not yet" label="Close with proof" tone="good" busy={save.isPending} onGo={go} />}>
      <p style={{ marginTop: 0 }}>
        Before this {props.noun} closes, attach something that proves it: the email you sent, the report, or a link.
        {props.noun === 'request' ? '' : ' The Ops associate director checks it.'}
      </p>
      <p className="sub" style={{ marginTop: -6 }}>
        <b>{props.title}</b>
      </p>
      <Field label="Paste a link" error={errs.proof_url}>
        <input className="in" value={link} disabled={none} onChange={(e) => setLink(e.target.value)} placeholder="e.g. link to the email thread or shared folder" inputMode="url" />
      </Field>
      <label className="split" style={{ margin: '2px 0 10px', alignItems: 'flex-start' }}>
        <input type="checkbox" checked={none} onChange={(e) => setNone(e.target.checked)} style={{ marginTop: 4 }} />
        <span style={{ fontSize: 13 }}>
          <b>No attachment needed for this {props.noun}.</b>{' '}
          <span className="sub">Close it with a one-line note instead, for example when the work was a call or a quick check.</span>
        </span>
      </label>
      <Field label="What was done" error={errs.proof_text ?? errs.note}>
        <textarea className="in" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="One line is enough" />
      </Field>
      {local ? (
        <div className="ferr" role="alert">
          {local}
        </div>
      ) : null}
    </Modal>
  );
}

// ---------------------------------------------------------------- log a client request

export function LogClientRequestModal({ onClose, clientId, onCreated }: { onClose: Close; clientId?: string; onCreated?: (id: string) => void }) {
  const { errs, fail, toast } = useFail(['text', 'client_id', 'via']);
  const clients = useClients();
  const [text, setText] = useState('');
  const [client, setClient] = useState(clientId ?? '');
  const [via, setVia] = useState<RequestVia>('Email');
  const key = useMemo(newIdempotencyKey, []);
  const pick = client || clients.data?.[0]?.id || '';
  const save = useWorkWrite(() => call('clientRequests.create', { idempotencyKey: key, body: { client_id: pick, via, text: text.trim() } }));
  const go = () => {
    if (text.trim().length < 3) return toast('Describe the request');
    if (!pick) return toast('Pick the client');
    save.mutate(undefined, {
      onSuccess: (r) => {
        toast(r.owner ? `Logged. Assigned to ${firstName(r.owner.name)}` : 'Logged');
        onCreated?.(r.id);
        onClose();
      },
      onError: fail,
    });
  };
  return (
    <Modal title="Log a client request" sub="The response clock starts now." onClose={onClose} footer={<Foot onClose={onClose} label="Log request" busy={save.isPending} onGo={go} />}>
      <Field label="What did they ask for" error={errs.text}>
        <textarea className="in" value={text} onChange={(e) => setText(e.target.value)} autoFocus />
      </Field>
      <div className="cols c2">
        <Field label="Client" error={errs.client_id}>
          <select className="in" value={pick} onChange={(e) => setClient(e.target.value)}>
            {(clients.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Came in by" error={errs.via}>
          <select className="in" value={via} onChange={(e) => setVia(e.target.value as RequestVia)}>
            {(['Email', 'Call', 'WhatsApp', 'Meeting'] as RequestVia[]).map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- tell Ops what you need

/**
 * The requester's form ("Tell Ops what you need") and, for managers, "Log a request from Sales or Marketing"
 * with an extra "Asked by". Options (clients, Ops people, kinds, purposes) come from GET /internal-requests/options.
 */
export function InternalRequestModal({ onClose, onCreated }: { onClose: Close; onCreated?: (r: InternalRequest) => void }) {
  const { can } = useSession();
  const onBehalf = can('requests.triage');
  const { errs, fail, toast } = useFail(['on_behalf_of_id', 'text', 'type', 'client_id', 'prospect', 'purpose', 'needed_by_days', 'why_it_matters', 'wanted_spoc_id', 'file_keys']);
  const files = useRequestFiles();
  const opts = useRequestOptions();
  const users = useUsers(onBehalf && can('users.read'));
  const askers = (users.data ?? []).filter((u) => u.status === 'active' && (u.role === 'sales' || u.role === 'marketing'));
  const [by, setBy] = useState('');
  const [text, setText] = useState('');
  const [type, setType] = useState<InternalRequestType>('usage_summary');
  const [client, setClient] = useState('');
  const [prospect, setProspect] = useState('');
  const [purpose, setPurpose] = useState<RequestPurpose | ''>('');
  const [need, setNeed] = useState<NeedDays>(3);
  const [why, setWhy] = useState('');
  const [spoc, setSpoc] = useState('');
  const key = useMemo(newIdempotencyKey, []);
  const purposes = opts.data?.purposes ?? [];
  const purposeVal = (purpose || purposes[0]?.key || 'client_meeting') as RequestPurpose;
  const asker = by || askers[0]?.id || '';

  const save = useWorkWrite(() =>
    call('internalRequests.create', {
      idempotencyKey: key,
      body: {
        type,
        text: text.trim(),
        purpose: purposeVal,
        needed_by_days: need,
        why_it_matters: why.trim(),
        ...(client ? { client_id: client } : prospect.trim() ? { prospect: prospect.trim() } : {}),
        ...(spoc ? { wanted_spoc_id: spoc } : {}),
        ...(onBehalf && asker ? { on_behalf_of_id: asker } : {}),
        ...(files.keys.length ? { file_keys: files.keys } : {}),
      },
    }),
  );
  const go = () => {
    if (!text.trim()) return toast('Say what you need first');
    if (text.trim().length < 10) return toast('Say a little more about what you need');
    if (why.trim().length < 3) return toast('Say why that date matters');
    if (onBehalf && !asker) return toast('Pick who asked for it');
    if (files.busy) return toast('Wait for the files to finish uploading');
    if (files.failed) return toast('A file did not upload. Press Retry on it, or remove it');
    save.mutate(undefined, {
      onSuccess: (r) => {
        toast('Sent to Ops. You will see updates here.');
        onCreated?.(r);
        onClose();
      },
      onError: fail,
    });
  };
  return (
    <Modal
      wide
      title={onBehalf ? 'Log a request from Sales or Marketing' : 'Tell Ops what you need'}
      sub="Say it in your own words, then add the few details that save questions later."
      onClose={onClose}
      footer={<Foot onClose={onClose} label="Send to Ops" busy={save.isPending} onGo={go} />}
    >
      {onBehalf ? (
        <Field label="Asked by" error={errs.on_behalf_of_id}>
          <select className="in" value={asker} onChange={(e) => setBy(e.target.value)}>
            {askers.length ? null : <option value="">No one in Sales or Marketing yet</option>}
            {askers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}, {u.role === 'sales' ? 'Sales' : 'Marketing'}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      <Field label="What do you need?" error={errs.text}>
        <textarea
          className="in"
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. Kestrel wants to see how their collections journey is doing before Friday's call. Can I get the last 3 months of usage in a one-pager?"
          autoFocus
        />
      </Field>
      {/* The prototype's 'Speak it' (irSpeak) is deferred to Step 9: it used the Web Speech API, which in Chrome sends the
          microphone audio to Google, and ADR-002 allows only our own open-source STT. Typing is the design's own fallback. */}
      <Field label="What kind of request?" error={errs.type} hint={INTERNAL_REQUEST_TYPE_LABEL[type].help}>
        <select className="in" value={type} onChange={(e) => setType(e.target.value as InternalRequestType)}>
          {(opts.data?.types ?? Object.entries(INTERNAL_REQUEST_TYPE_LABEL).map(([k, v]) => ({ key: k as InternalRequestType, label: v.label }))).map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </select>
      </Field>
      <div className="cols c2">
        <Field label="Which client?" error={errs.client_id}>
          <select
            className="in"
            value={client}
            onChange={(e) => {
              setClient(e.target.value);
              if (e.target.value) setProspect('');
              // Prototype: choosing a client preselects its SPOC as the doer (still changeable below).
              const sp = opts.data?.clients.find((c) => c.id === e.target.value)?.spoc;
              setSpoc(sp && opts.data?.ops_people.some((p) => p.id === sp.id) ? sp.id : '');
            }}
          >
            <option value="">Not an existing client</option>
            {(opts.data?.clients ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        {client ? (
          <div />
        ) : (
          <Field label="Or the prospect" error={errs.prospect}>
            <input className="in" value={prospect} onChange={(e) => setProspect(e.target.value)} placeholder="e.g. Lakshmi Finserv" />
          </Field>
        )}
      </div>
      <div className="cols c2">
        <Field label="What is it for?" error={errs.purpose}>
          <select className="in" value={purposeVal} onChange={(e) => setPurpose(e.target.value as RequestPurpose)}>
            {purposes.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Needed by" error={errs.needed_by_days}>
          <select className="in" value={need} onChange={(e) => setNeed(+e.target.value as NeedDays)}>
            {NEED_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {needLabel(d)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <label className="fld">
        <span>
          Why does that date matter? <span className="sub">(a meeting, a proposal, something promised)</span>
        </span>
        <input className="in" value={why} onChange={(e) => setWhy(e.target.value)} placeholder="e.g. Presenting to their CX head on Thursday at 4 pm" />
        {errs.why_it_matters ? <small className="ferr">{errs.why_it_matters}</small> : null}
      </label>
      <div className="cols c2">
        <Field label="Who in Ops should do it?" error={errs.wanted_spoc_id}>
          <select className="in" value={spoc} onChange={(e) => setSpoc(e.target.value)}>
            <option value="">Let the Ops associate director decide</option>
            {(opts.data?.ops_people ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <label className="fld">
          <span>
            Attach files <span className="sub">(optional)</span>
          </span>
          <input
            className="in"
            type="file"
            multiple
            disabled={files.list.length >= REQUEST_FILE_MAX_COUNT}
            onChange={(e) => {
              files.add(Array.from(e.target.files ?? []), toast);
              e.target.value = '';
            }}
          />
          {errs.file_keys ? <small className="ferr">{errs.file_keys}</small> : null}
        </label>
      </div>
      {files.list.length ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '-4px 0 12px' }} aria-label="Attached files">
          {files.list.map((f) => (
            <span key={f.id} className={`chip ${f.state === 'error' ? 'cold' : f.state === 'done' ? 'info' : ''}`} title={f.error ?? f.file.name}>
              {f.state === 'uploading' ? 'Uploading… ' : f.state === 'error' ? 'Did not upload: ' : ''}
              {f.file.name}
              {f.state === 'error' ? (
                <button
                  type="button"
                  onClick={() => files.retry(f.id, toast)}
                  aria-label={`Try ${f.file.name} again`}
                  style={{ border: 0, background: 'none', marginLeft: 6, cursor: 'pointer', color: 'inherit', padding: 0, fontWeight: 600, textDecoration: 'underline' }}
                >
                  Retry
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => files.remove(f.id)}
                aria-label={`Remove ${f.file.name}`}
                style={{ border: 0, background: 'none', marginLeft: 6, cursor: 'pointer', color: 'inherit', padding: 0 }}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <div className="note" style={{ marginBottom: 4 }}>
        Ops will acknowledge within 24 hours. You will see it here the moment someone picks it up.
      </div>
    </Modal>
  );
}

type PickedFile = { id: string; file: File; state: 'uploading' | 'done' | 'error'; key?: string; error?: string };

/**
 * The prototype's "Attach files (optional)": each picked file uploads at once (sign-upload, then PUT), so Send
 * only carries keys. Over 25 MB is refused before anything is sent, in the contract's own words; at most 5.
 */
function useRequestFiles() {
  const [list, setList] = useState<PickedFile[]>([]);
  const patch = (id: string, p: Partial<PickedFile>) => setList((l) => l.map((x) => (x.id === id ? { ...x, ...p } : x)));
  const add = (picked: File[], say: (m: string) => void) => {
    const room = REQUEST_FILE_MAX_COUNT - list.length;
    const big = picked.filter(requestFileTooBig);
    const ok = picked.filter((f) => !requestFileTooBig(f));
    if (big.length) say(`${big.map((f) => f.name).join(', ')}: ${REQUEST_FILE_TOO_BIG}`);
    if (ok.length > room) say(`Attach at most ${REQUEST_FILE_MAX_COUNT} files`);
    for (const file of ok.slice(0, Math.max(0, room))) {
      const id = newIdempotencyKey();
      setList((l) => [...l, { id, file, state: 'uploading' }]);
      upload(id, file, say);
    }
  };
  const upload = (id: string, file: File, say: (m: string) => void) =>
    uploadRequestFile(file).then(
      (key) => patch(id, { state: 'done', key, error: undefined }),
      (e) => {
        patch(id, { state: 'error', error: errorText(e) });
        say(`${file.name} did not upload. ${errorText(e)}`);
      },
    );
  /** Try a failed file again, in its place in the list (the same file; no re-pick). */
  const retry = (id: string, say: (m: string) => void) => {
    const f = list.find((x) => x.id === id);
    if (!f || f.state !== 'error') return;
    patch(id, { state: 'uploading', error: undefined });
    upload(id, f.file, say);
  };
  return {
    list,
    add,
    retry,
    remove: (id: string) => setList((l) => l.filter((x) => x.id !== id)),
    keys: list.flatMap((x) => (x.state === 'done' && x.key ? [x.key] : [])),
    busy: list.some((x) => x.state === 'uploading'),
    failed: list.some((x) => x.state === 'error'),
  };
}

// ---------------------------------------------------------------- triage

const TEAM = (t: 'sales' | 'marketing') => (t === 'sales' ? 'Sales' : 'Marketing');

export function TriageModal({ r, onClose }: { r: InternalRequest; onClose: Close }) {
  const { errs, fail, toast } = useFail(['owner_id', 'expected_days', 'note']);
  const opts = useRequestOptions();
  const whose = useWhose(true);
  const today = todayIso();
  const people = opts.data?.ops_people ?? [];
  const load = (id: string) => whose.data?.data.find((w) => w.user.id === id)?.open ?? 0;
  const least = [...people].sort((a, b) => load(a.id) - load(b.id))[0];
  const [owner, setOwner] = useState('');
  const needDays = Math.max(0, daysBetween(today, r.needed_by));
  const nearest = NEED_OPTIONS.find((d) => d >= needDays) ?? 14;
  const [exp, setExp] = useState<NeedDays>(nearest);
  const [note, setNote] = useState('');
  const pick = owner || r.wanted_spoc?.id || least?.id || '';
  const who = firstName(r.by.name);
  const save = useWorkWrite((action: 'accept' | 'ask_details' | 'decline') =>
    call('internalRequests.triage', {
      params: { id: r.id },
      idempotencyKey: newIdempotencyKey(),
      body: action === 'accept' ? { action, owner_id: pick, expected_days: exp, ...(note.trim() ? { note: note.trim() } : {}) } : { action, note: note.trim() },
    }),
  );
  const go = (action: 'accept' | 'ask_details' | 'decline') => {
    if (action !== 'accept' && note.trim().length < 3) return toast(`Add a note so ${who} knows why`);
    if (action === 'accept' && !pick) return toast('Pick who in Ops will do it');
    save.mutate(action, {
      onSuccess: (res) => {
        const o = res.request.owner;
        toast(action === 'accept' ? `Accepted. Task created for ${o ? firstName(o.name) : 'the owner'}` : action === 'ask_details' ? `Asked ${who} for details` : `Declined, and ${who} can see why`);
        onClose();
      },
      onError: (e) => {
        if (e instanceof ApiFailure && e.status === 409) {
          toast('Someone already triaged this request');
          onClose();
        } else fail(e);
      },
    });
  };
  return (
    <Modal
      title="Triage"
      sub={
        <>
          <b>{r.by.name}</b> ({TEAM(r.team)}) asked: {r.text}
        </>
      }
      onClose={onClose}
      footer={
        <>
          <button className="btn bad" onClick={() => go('decline')} disabled={save.isPending}>
            Decline
          </button>
          <button className="btn" onClick={() => go('ask_details')} disabled={save.isPending}>
            Ask for details
          </button>
          <div className="grow" />
          <button className="btn good" onClick={() => go('accept')} disabled={save.isPending} aria-busy={save.isPending}>
            Accept and assign
          </button>
        </>
      }
    >
      <Field label="Who takes it (least loaded is pre-selected)" error={errs.owner_id}>
        <select className="in" value={pick} onChange={(e) => setOwner(e.target.value)}>
          {people.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({load(p.id)} open)
            </option>
          ))}
        </select>
      </Field>
      <label className="fld">
        <span>
          Expected by{' '}
          <span className="sub">
            {TEAM(r.team)} needs it {needLower(dateNeedLabel(r.needed_by, today))}
          </span>
        </span>
        <select className="in" value={exp} onChange={(e) => setExp(+e.target.value as NeedDays)}>
          {NEED_OPTIONS.map((d) => (
            <option key={d} value={d}>
              {needLabel(d)}
            </option>
          ))}
        </select>
        {errs.expected_days ? <small className="ferr">{errs.expected_days}</small> : null}
      </label>
      <Field label={`Note to ${who}`} error={errs.note}>
        <textarea className="in" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Required if you ask for details or decline" />
      </Field>
    </Modal>
  );
}

// ---------------------------------------------------------------- running late / not quite yet

export function DelayModal({ r, onClose }: { r: InternalRequest; onClose: Close }) {
  const { errs, fail, toast } = useFail(['expected_days', 'why']);
  const [d, setD] = useState<NeedDays>(NEED_OPTIONS[0]!);
  const [why, setWhy] = useState('');
  const who = firstName(r.by.name);
  const save = useWorkWrite(() =>
    call('internalRequests.delay', { params: { id: r.id }, idempotencyKey: newIdempotencyKey(), ifMatch: r.version, body: { expected_days: d, why: why.trim() } }),
  );
  const go = () => {
    if (why.trim().length < 3) return toast('Add a short reason');
    save.mutate(undefined, {
      onSuccess: () => {
        toast(`Sent to ${who}`);
        onClose();
      },
      onError: fail,
    });
  };
  return (
    <Modal title="Running late?" sub={`Tell ${who} the new date and why. They see it straight away.`} onClose={onClose} footer={<Foot onClose={onClose} label={`Tell ${who}`} busy={save.isPending} onGo={go} />}>
      <Field label="New expected date" error={errs.expected_days}>
        <select className="in" value={d} onChange={(e) => setD(+e.target.value as NeedDays)}>
          {NEED_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {needLabel(o)}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Why" error={errs.why}>
        <textarea className="in" rows={2} value={why} onChange={(e) => setWhy(e.target.value)} placeholder="e.g. Waiting for Kestrel’s data export" />
      </Field>
    </Modal>
  );
}

export function NotYetModal({ r, onClose }: { r: InternalRequest; onClose: Close }) {
  const { errs, fail, toast } = useFail(['what_is_missing']);
  const [w, setW] = useState('');
  const save = useWorkWrite(() =>
    call('internalRequests.confirm', { params: { id: r.id }, idempotencyKey: newIdempotencyKey(), body: { outcome: 'not_yet', what_is_missing: w.trim() } }),
  );
  const go = () => {
    if (w.trim().length < 3) return toast('Say what is missing');
    save.mutate(undefined, {
      onSuccess: () => {
        toast('Sent back to Ops');
        onClose();
      },
      onError: fail,
    });
  };
  return (
    <Modal
      title="What is still missing?"
      sub={`${r.owner ? firstName(r.owner.name) : 'Ops'} will pick it up again.`}
      onClose={onClose}
      footer={<Foot onClose={onClose} label="Send back to Ops" busy={save.isPending} onGo={go} />}
    >
      <Field label="What needs to change" error={errs.what_is_missing}>
        <textarea className="in" rows={3} value={w} onChange={(e) => setW(e.target.value)} placeholder="e.g. I also need the Q2 numbers split by product" autoFocus />
      </Field>
    </Modal>
  );
}

/** "Yes, it is done" / "Confirm done": one call, no pop-up. */
export function useConfirmDone() {
  const toast = useToast();
  const m = useWorkWrite((id: string) => call('internalRequests.confirm', { params: { id }, idempotencyKey: newIdempotencyKey(), body: { outcome: 'done' } }));
  return {
    busy: m.isPending,
    confirm: (id: string, after?: () => void) =>
      m.mutate(id, {
        onSuccess: () => {
          toast('Thanks. Closed.');
          after?.();
        },
        onError: (e) => toast(errorText(e)),
      }),
  };
}

// ---------------------------------------------------------------- raise / promote an escalation

export function EscalationModal({ onClose, from, clientId }: { onClose: Close; clientId?: string; from?: { requestId: string; text: string; clientId: string } }) {
  const { errs, fail, toast } = useFail(['title', 'note', 'client_id', 'severity']);
  const clients = useClients();
  const [text, setText] = useState(from?.text ?? '');
  const [client, setClient] = useState(from?.clientId ?? clientId ?? '');
  const [sev, setSev] = useState<Severity>('med');
  const key = useMemo(newIdempotencyKey, []);
  const pick = client || clients.data?.[0]?.id || '';
  const t = text.trim();
  // `title` holds 200 characters; a longer account goes in whole as the first note.
  const title = t.length > 200 ? `${t.slice(0, 197)}…` : t;
  const note = t.length > 200 ? t : undefined;
  const save = useWorkWrite(() =>
    from
      ? call('clientRequests.escalate', { params: { id: from.requestId }, idempotencyKey: key, body: { severity: sev, title, ...(note ? { note } : {}) } })
      : call('escalations.create', { idempotencyKey: key, body: { client_id: pick, severity: sev, title, ...(note ? { note } : {}) } }),
  );
  const go = () => {
    if (t.length < 3) return toast('Describe what happened');
    save.mutate(undefined, {
      onSuccess: () => {
        toast('Escalation raised');
        onClose();
      },
      onError: (e) => {
        if (e instanceof ApiFailure && e.status === 409) {
          toast('This request already has an open escalation');
          onClose();
        } else fail(e);
      },
    });
  };
  return (
    <Modal
      title={from ? 'Promote to escalation' : 'Raise an escalation'}
      sub={from ? 'From a client request. Check the wording, then raise it.' : 'The Ops associate director and founders see it straight away.'}
      onClose={onClose}
      footer={<Foot onClose={onClose} label="Raise" busy={save.isPending} onGo={go} />}
    >
      <Field label="What happened" error={errs.title ?? errs.note}>
        <textarea className="in" value={text} onChange={(e) => setText(e.target.value)} autoFocus />
      </Field>
      <div className="cols c2">
        <Field label="Client" error={errs.client_id}>
          <select className="in" value={pick} onChange={(e) => setClient(e.target.value)} disabled={!!from}>
            {(clients.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Severity" error={errs.severity}>
          <select className="in" value={sev} onChange={(e) => setSev(e.target.value as Severity)}>
            <option value="med">Medium</option>
            <option value="high">High</option>
            <option value="low">Low</option>
          </select>
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- add a client task

const DUE_CHOICES: [number, string][] = [
  [1, 'Tomorrow'],
  [3, 'In 3 days'],
  [7, 'In a week'],
  [14, 'In two weeks'],
];

/**
 * Prototype saveCommit(): the promise and its linked high-priority task belong to the client's SPOC (`by: c.spoc`,
 * `who: c.spoc`), whoever logs it. A manager (task.reassign) sends `promised_by_id` = the SPOC; for everyone else
 * the server already falls back to the caller and refuses anyone else, and their own clients are theirs anyway.
 */
export function AddClientTaskModal({ onClose, clientId }: { onClose: Close; clientId?: string }) {
  const { can, me } = useSession();
  const { errs, setErrs, fail, toast } = useFail(['what', 'client_id', 'due', 'promised_to', 'promised_by_id']);
  const clients = useClients();
  const [what, setWhat] = useState('');
  const [client, setClient] = useState(clientId ?? '');
  const [due, setDue] = useState(3);
  const [to, setTo] = useState<string | null>(null);
  const key = useMemo(newIdempotencyKey, []);
  const pick = client || clients.data?.[0]?.id || '';
  const detail = useClient(pick || null);
  const primary = detail.data?.contacts?.find((c) => c.is_primary) ?? detail.data?.contacts?.[0];
  const told = to ?? primary?.name ?? '';
  const spoc = clients.data?.find((c) => c.id === pick)?.spoc;
  const manager = can('task.reassign');
  // The server only gives work to an active Ops person (activeOps). A SPOC who was disabled but still holds the
  // client cannot take it, so it comes to the caller, and the pop-up says so before Add rather than failing.
  const users = useUsers(manager && can('users.read'));
  const spocUser = spoc ? users.data?.find((u) => u.id === spoc.id) : undefined;
  const spocOff = !!spocUser && (spocUser.status !== 'active' || !OPS_ROLES.includes(spocUser.role));
  const forSpoc = !!spoc && spoc.id !== me.user.id && manager && !spocOff;
  const save = useWorkWrite(() =>
    call('clientTasks.create', {
      idempotencyKey: key,
      body: {
        client_id: pick,
        what: what.trim(),
        promised_to: told.trim(),
        due: addDays(todayIso(), due),
        source: `Logged by ${firstName(me.user.name)}`,
        ...(forSpoc ? { promised_by_id: spoc.id } : {}),
      },
    }),
  );
  const go = () => {
    if (what.trim().length < 3) return toast('Describe the task');
    if (told.trim().length < 2) return toast('Say which contact at the client is waiting');
    save.mutate(undefined, {
      onSuccess: (r) => {
        toast(`Client task added. A task was created for ${firstName(r.task.assignee?.name ?? r.client_task.promised_by.name)}.`);
        onClose();
      },
      onError: fail,
    });
  };
  return (
    <Modal title="Add a client task" sub="Anything a client is waiting on from us." onClose={onClose} footer={<Foot onClose={onClose} label="Save task" busy={save.isPending} onGo={go} />}>
      <Field label="What is the task" error={errs.what}>
        <input className="in" value={what} onChange={(e) => setWhat(e.target.value)} autoFocus />
      </Field>
      <div className="cols c2">
        <Field
          label="Client"
          error={errs.client_id ?? (errs.promised_by_id ? `${spoc ? firstName(spoc.name) : 'The SPOC'}, this client’s SPOC, is not active. Change the client’s SPOC first.` : undefined)}
          hint={spocOff && spoc ? `${firstName(spoc.name)}, this client’s SPOC, is not active, so this comes to you.` : undefined}
        >
          <select
            className="in"
            value={pick}
            onChange={(e) => {
              setClient(e.target.value);
              setTo(null);
              setErrs({});
            }}
          >
            {(clients.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Due" error={errs.due}>
          <select className="in" value={due} onChange={(e) => setDue(+e.target.value)}>
            {DUE_CHOICES.map(([d, l]) => (
              <option key={d} value={d}>
                {l}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {/* Prototype newCommit has no such field: saveCommit tells the client's primary contact. The contract needs
          promised_to, so it is asked only when the client has no contact on file. */}
      {!detail.isLoading && !primary ? (
        <Field label="Who at the client is waiting" error={errs.promised_to}>
          <input className="in" value={told} onChange={(e) => setTo(e.target.value)} placeholder="e.g. Anita, Head of CX" />
        </Field>
      ) : null}
    </Modal>
  );
}

// ---------------------------------------------------------------- + Add chooser (prototype workAdd)

type AddKind = 'task' | 'request' | 'escalation' | 'approval' | 'client_task' | 'internal' | 'history';

export function AddChooser({ onClose, clientId }: { onClose: Close; clientId?: string }) {
  const { can, readOnly } = useSession();
  const [kind, setKind] = useState<AddKind | null>(null);
  if (kind === 'task') return <AddTaskModal onClose={onClose} clientId={clientId} />;
  if (kind === 'request') return <LogClientRequestModal onClose={onClose} clientId={clientId} />;
  if (kind === 'escalation') return <EscalationModal onClose={onClose} clientId={clientId} />;
  if (kind === 'approval') return <NewItemModal onClose={onClose} clientId={clientId} />;
  if (kind === 'client_task') return <AddClientTaskModal onClose={onClose} clientId={clientId} />;
  if (kind === 'internal') return <InternalRequestModal onClose={onClose} />;
  if (kind === 'history') return <HistoryImportModal onClose={onClose} />;
  const cards: [AddKind, string, string][] = [
    ['task', 'A task', 'Work for a SPOC, one-off or repeating'],
    ['request', 'A client request', 'Something a client asked for. The response clock starts'],
    ['escalation', 'An escalation', 'Something has gone wrong for a client'],
    // Prototype workAdd order: after An escalation. Same gate as the Approvals board's "New item" (documents.create:
    // managers or the client's SPOC, not while viewing as someone), so it is never offered to someone the server refuses.
    ...(!readOnly && (can('clients.read.all') || can('clients.read.own'))
      ? ([['approval', 'An item for approval', 'An MIS, deck or report going to a client']] as [AddKind, string, string][])
      : []),
    ['client_task', 'A client task', 'Something a client is waiting on from us, with a date'],
    ...(can('requests.triage') ? ([['internal', 'A request from Sales or Marketing', 'Log it for them. Their response clock starts']] as [AddKind, string, string][]) : []),
    // Prototype workAdd: last, for managers only (isMgr). Same gate as every /tasks/history-import route (task.reassign).
    ...(can('task.reassign')
      ? ([['history', 'Old tasks or old escalations', 'Import history from a sheet, so the record starts from the past']] as [AddKind, string, string][])
      : []),
  ];
  return (
    // As the approved prototype renders workAdd: the choices sit in the grey band at the foot of the pop-up,
    // right-aligned and sized to their text, under an empty body band.
    <Modal
      title="Add"
      sub="Pick what you are adding. It all lands in this one list."
      onClose={onClose}
      footer={
        <div style={{ display: 'grid', gap: 8, justifyItems: 'end', width: '100%' }}>
          {cards.map(([k, t, s]) => (
            <button key={k} className="btn" style={{ textAlign: 'left', padding: '10px 14px', display: 'block', maxWidth: '100%' }} onClick={() => setKind(k)}>
              <b>{t}</b>
              <div className="sub" style={{ fontWeight: 400, fontSize: 12.5 }}>
                {s}
              </div>
            </button>
          ))}
        </div>
      }
    >
      {null}
    </Modal>
  );
}
